import {useEffect, useState} from 'react'
import {useInAppCall, CALL_STATE} from '@/hooks/useInAppCall'
import {chatService} from '@/services/chatService'
import {isTripCallable} from '@/utils/callable'
import CallOverlay from '@/customComponents/trackDriver/CallOverlay.jsx'
import {driverPlaceHolder} from '@img/index.js'

/**
 * WHO IS COMING FOR YOU — the person on one row, the car on the other.
 *
 * ── ⚠️ NO HEADING, AND THAT IS THE DESIGN ─────────────────────────────────────────
 * A face, a name and a car need no label telling you what they are. The card sits under a
 * greeting that has already said a driver is waiting, so "Your driver" was a line of chrome
 * repeating it.
 *
 * ── ⚠️ TWO ROWS, SPLIT BY A FULL-WIDTH RULE ───────────────────────────────────────
 * Person above, vehicle below, each with its identity on the left and its action or
 * identifier on the right. The rule runs edge to edge rather than inset, so the two rows
 * read as two separate answers rather than as one list.
 *
 * ── ⚠️ THE PLATE IS THE VEHICLE ROW'S RIGHT-HAND ANCHOR ───────────────────────────
 * A passenger walking out to a kerb matches a plate against a car, so it sits opposite the
 * car in its own pill rather than being stacked with the model name. Model and colour stay
 * on the left, next to the picture they describe.
 *
 * ── ⚠️ CALLING GOES THROUGH THE MASKING BRIDGE, NOT A `tel:` LINK ─────────────────
 * Neither side of a ride has the other's number, so a phone link would either dial nothing
 * or expose a real one. This is the same in-app call the tracking page's driver card
 * places, including its own screen — a call placed inside the page has no ringing, no mute
 * and, worst of all, no way to hang up.
 */

const ChatGlyph = () => (
    <svg className="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M21 11.5a8.4 8.4 0 0 1-9 8.4 8.5 8.5 0 0 1-3.8-.9L3 21l2-5.2A8.4 8.4 0 0 1 12 3a8.4 8.4 0 0 1 9 8.5z"/>
    </svg>
)

const CallGlyph = () => (
    <svg className="h-6 w-6" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M6.6 10.8a15.1 15.1 0 0 0 6.6 6.6l2.2-2.2a1 1 0 0 1 1-.24c1.1.37 2.3.57 3.6.57a1 1 0 0 1 1 1V20a1 1 0 0 1-1 1A17 17 0 0 1 3 4a1 1 0 0 1 1-1h3.5a1 1 0 0 1 1 1c0 1.3.2 2.5.57 3.6a1 1 0 0 1-.25 1z"/>
    </svg>
)

const StarGlyph = () => (
    <svg className="h-5 w-5 max-md:h-4 max-md:w-4" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="m12 3.6 2.6 5.3 5.9.9-4.3 4.1 1 5.8-5.2-2.7-5.2 2.7 1-5.8L3.5 9.8l5.9-.9z"/>
    </svg>
)

/** Mint disc, dark glyph — the arrival flow's own action affordance. */
const ACTION = 'flex h-14 w-14 max-md:h-8 max-md:w-8 shrink-0 cursor-pointer items-center justify-center rounded-full border-0 bg-(--accent-green-light) p-0 text-(--text-primary) disabled:cursor-not-allowed disabled:opacity-50'

export default function PickupDriverCard({trip, chatOpen = false, onOpenChat}) {
    /* ⚠️ BEFORE THE EARLY RETURN, because hooks cannot live behind one. */
    const {state, error, muted, busy, start, hangUp, toggleMute} = useInAppCall(trip?.id)
    const callable = isTripCallable(trip)

    const [unread, setUnread] = useState(0)

    /*
     * ⚠️ THE BADGE COMES FROM ITS OWN ENDPOINT, NEVER FROM THE CONVERSATION. Fetching the
     * thread marks the driver's messages read as a side effect, so counting them from it
     * would clear the very number being reported — the badge would drop to zero on its own
     * while nobody had read anything.
     *
     * ⚠️ AND IT IS READ ON OPEN AND ON RETURN TO THE TAB, not on a timer. A count a few
     * seconds stale costs nothing; a request every few seconds for a whole arrival costs a
     * great deal. Same rule as the tracking page's driver card.
     */
    useEffect(() => {
        if (!trip?.id) return undefined
        let cancelled = false
        const refresh = () => {
            // Nothing to count while the thread is open — reading it marks them read.
            if (chatOpen) { setUnread(0); return }
            chatService.unreadCount(trip.id).then((n) => { if (!cancelled) setUnread(n) })
        }
        refresh()

        const onVisible = () => { if (document.visibilityState === 'visible') refresh() }
        document.addEventListener('visibilitychange', onVisible)
        return () => {
            cancelled = true
            document.removeEventListener('visibilitychange', onVisible)
        }
    }, [trip?.id, chatOpen])

    const driver = trip?.driver
    /* ⚠️ NOTHING RATHER THAN A PLACEHOLDER. A card with an empty avatar and no name, on the
       screen that has just said "your driver is waiting", is worse than the space it fills. */
    if (!driver?.name) return null

    const vehicle = driver.vehicle
    const vehicleLine = [vehicle?.make, vehicle?.model].filter(Boolean).join(' ')
    /* Straight from `TripResource.driver` — nothing defaulted, nothing computed. */
    const rating = Number(driver.average_rating)
    const hasRating = Number.isFinite(rating) && rating > 0

    return (
        <>
            {/* `p-0` then per-row padding: the rule has to reach both edges, which it
                cannot do inside a padded box. */}
            <div className="white-global-section p-0! overflow-hidden max-md:p-3! max-md:shadow-none! max-md:rounded-2xl! max-md:border max-md:border-(--border-color)">
                <div className="flex items-center gap-4 p-6 max-md:gap-3 max-md:p-0">
                    <img
                        src={driver.photo || driverPlaceHolder}
                        alt=""
                        className="h-16 w-16 shrink-0 rounded-full object-cover max-md:h-10 max-md:w-10"
                    />

                    <div className="min-w-0 flex-1">
                        <p className="mb-0! truncate text-xl font-bold leading-tight max-md:whitespace-normal max-md:break-words max-md:text-sm">{driver.name}</p>
                        {hasRating && (
                            <span className="mt-1 flex items-center gap-1.5 text-lg text-(--text-primary) max-md:mt-0.5 max-md:text-sm">
                                {/* Amber, the one warm colour on the page — a rating is the
                                    only thing here that is an opinion rather than a fact. */}
                                <span className="text-[#F5A524]"><StarGlyph/></span>
                                {rating.toFixed(1)}
                            </span>
                        )}
                    </div>

                    <span className="relative shrink-0">
                        <button
                            type="button"
                            onClick={onOpenChat}
                            aria-label={`Message ${driver.name}`}
                            className={ACTION}
                        >
                            <ChatGlyph/>
                        </button>
                        {unread > 0 && (
                            <span className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-(--text-red) px-1 text-[11px] font-semibold text-white">
                                {unread > 9 ? '9+' : unread}
                            </span>
                        )}
                    </span>

                    {/* ⚠️ SHOWN AND DISABLED, never hidden. The bridge needs a driver leg to
                        connect to and has none outside the call window, so the honest state
                        is an unavailable button rather than one that errors at the moment
                        somebody needs it — or a control that silently is not there. */}
                    <button
                        type="button"
                        onClick={start}
                        disabled={busy || !callable}
                        aria-label={`Call ${driver.name}`}
                        title={callable ? 'Call your driver' : 'Calling is not available for this trip yet'}
                        className={ACTION}
                    >
                        <CallGlyph/>
                    </button>
                </div>

                {vehicle && (vehicleLine || vehicle.plate_no) && (
                    <>
                        <div className="h-px bg-(--border-color) max-md:my-3"/>

                        <div className="flex items-center gap-4 p-6 max-md:gap-3 max-md:p-0">
                            {vehicle.image
                                ? <img src={vehicle.image} alt="" className="h-16 w-28 shrink-0 object-contain max-md:h-10 max-md:w-16"/>
                                : (
                                    <span className="flex h-16 w-28 shrink-0 items-center justify-center rounded-(--radius-md) bg-(--bg-grey) max-md:h-10 max-md:w-16">
                                        <svg className="h-7 w-7 text-(--text-secondary)" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                            <path d="M5 17h14M4 17v-4l2-5h12l2 5v4M4 17v2h3v-2M17 17v2h3v-2"/>
                                            <circle cx="7.5" cy="13.5" r="1"/>
                                            <circle cx="16.5" cy="13.5" r="1"/>
                                        </svg>
                                    </span>
                                )}

                            <div className="min-w-0 flex-1">
                                {vehicleLine && <p className="mb-0! truncate text-xl font-bold leading-tight max-md:whitespace-normal max-md:break-words max-md:text-sm">{vehicleLine}</p>}
                                {/* ⚠️ `color` IS NULLABLE ON THE RESOURCE and is simply absent
                                    for a vehicle that has none recorded — no placeholder, no
                                    dash. A colour is only worth printing when it is a real
                                    one a passenger can match against a car at a kerb. */}
                                {vehicle.color && <span className="block truncate text-(--text-secondary) max-md:whitespace-normal max-md:text-xs">{vehicle.color}</span>}
                            </div>

                            {vehicle.plate_no && (
                                <span className="shrink-0 rounded-full bg-(--bg-grey) px-6 py-3 text-lg font-medium text-(--text-primary) max-md:px-3 max-md:py-1.5 max-md:text-sm">
                                    {vehicle.plate_no}
                                </span>
                            )}
                        </div>
                    </>
                )}
            </div>

            {/* ⚠️ THE CALL HAS A SCREEN OF ITS OWN, and it has to — see the note above. */}
            {state !== CALL_STATE.idle && (
                <CallOverlay
                    driverName={driver.name}
                    driverPhoto={driver.photo}
                    state={state}
                    error={error}
                    muted={muted}
                    onHangUp={hangUp}
                    onToggleMute={toggleMute}
                    onRetry={start}
                />
            )}
        </>
    )
}

export {PickupDriverCard}
